<script lang="ts">
    import { createEventDispatcher, onMount, afterUpdate } from 'svelte';
    import Table from '../../Table.svelte';
    // import SearchInput from '../../SearchInput.svelte';
    // import DateRange from '../../DateRange.svelte';
    import StatusBadge from '../../StatusBadge.svelte';
    import type { PaymentItem, PaginationInfo } from '../../../lib/api/employer/charity';

    export let payments: PaymentItem[] = [];
    export let paging: PaginationInfo;

    const dispatch = createEventDispatcher();

    // Search state
    // let searchPhrase = '';
    // let startDate = '';
    // let endDate = '';
    let filteredPayments: any[] = [];
    let error: string | null = null;

    // Function to transform payments data
    function transformPayments(payments: any[]) {
        if (payments.length > 0) {
            return payments.map(payment => ({
                uuid: payment.payout_uuid,
                id: payment.payout_uuid,
                reference_number: payment.reference_number || 'N/A',
                amount: payment.amount,
                transaction_date: payment.transaction_date,
                status: payment.status || 'PENDING',
                notes: payment.notes
            }));
        }
        return [];
    }

    // Watch for changes in payments prop
    $: {
        filteredPayments = transformPayments(payments || []);
        // Don't set error for empty lists - let the UI show empty state instead
        error = null;
    }

    // Function to handle search
    // function handleSearch() {
    //     dispatch('updateFilters', {
    //         phrase: searchPhrase,
    //         page: 1
    //     });
    // }

    // Function to handle date range change
    // function handleDateRangeChange(event: CustomEvent) {
    //     startDate = event.detail.startDate;
    //     endDate = event.detail.endDate;
    //     dispatch('updateFilters', {
    //         date_from: startDate,
    //         date_to: endDate,
    //         page: 1
    //     });
    // }

    // Helper functions for data formatting
    function formatCurrency(value: number): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value || 0);
    }

    function formatDate(dateString: string): string {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (e) {
            return dateString || '-';
        }
    }

    // Table columns definition
    const columns = [
        {
            key: 'reference_number',
            label: 'Reference',
            width: '15%',
            render: (value: string) => value || 'N/A'
        },
        {
            key: 'amount',
            label: 'Amount',
            width: '15%',
            render: (value: number) => formatCurrency(value)
        },
        {
            key: 'transaction_date',
            label: 'Transaction Date',
            width: '15%',
            render: (value: string) => formatDate(value)
        },
        {
            key: 'status',
            label: 'Status',
            width: '18%',
            align: 'center',
            render: (value: string) => {
                return `<div class="status-badge-wrapper" data-status="${value}"></div>`;
            }
        },
        {
            key: 'notes',
            label: 'Notes',
            width: '40%',
            render: (value: string | null) => {
                if (!value) return '-';
                return value.length > 50 ? `${value.substring(0, 47)}...` : value;
            }
        }
    ];

    // Add status badges to rendered table
    afterUpdate(() => {
        const statusWrappers = document.querySelectorAll('.status-badge-wrapper');
        statusWrappers.forEach((wrapper) => {
            if (!wrapper.hasChildNodes()) {
                const status = wrapper.getAttribute('data-status');
                const statusBadge = document.createElement('div');
                wrapper.appendChild(statusBadge);
                
                // Create a new instance of StatusBadge component
                new StatusBadge({
                    target: statusBadge,
                    props: {
                        status: status ?? undefined
                    }
                });
            }
        });
    });

    onMount(() => {
        // Initial data transformation
        filteredPayments = transformPayments(payments);
    });
</script>

<div class="payments-list">
    <!-- Commented out filters section -->
    <!-- <div class="filters">
        <SearchInput
            placeholder="Search payments..."
            bind:value={searchPhrase}
            on:search={handleSearch}
            width="250px"
        />
        
        <DateRange
            bind:startDate
            bind:endDate
            placeholder="Date Range"
            on:apply={handleDateRangeChange}
        />
    </div> -->

    {#if error}
        <div class="error-message">
            {error}
        </div>
    {:else if filteredPayments.length > 0}
        <div class="table-container">
            <Table {columns} data={filteredPayments} />
        </div>

        {#if paging}
            <div class="pagination">
                <span class="pagination-info">
                    Showing {paging.from} to {paging.to} of {paging.total} payments
                </span>
            </div>
        {/if}
    {:else}
        <div class="no-data">
            <p>No payments found</p>
        </div>
    {/if}
</div>

<style>
    .payments-list {
        padding: 1.5rem;
    }

    /* Commented out filter styles */
    /* .filters {
        display: flex;
        gap: 1rem;
        margin-bottom: 1.5rem;
    } */

    .table-container {
        background: white;
        border-radius: 0.5rem;
        overflow: hidden;
    }

    .pagination {
        margin-top: 1rem;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 1rem;
    }

    .pagination-info {
        color: #6B7280;
        font-size: 0.875rem;
    }

    .error-message {
        text-align: center;
        padding: 2rem;
        color: #991B1B;
        background: #FEE2E2;
        border-radius: 0.5rem;
        margin-bottom: 1rem;
    }

    .no-data {
        text-align: center;
        padding: 3rem;
        color: #6B7280;
        background: white;
        border-radius: 0.5rem;
    }

    .no-data p {
        margin: 0;
        font-size: 1rem;
    }

    :global(.status-badge-wrapper) {
        display: inline-flex;
        align-items: center;
    }

    :global(.status-badge) {
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
        text-transform: uppercase;
    }

    :global(.status-approved) {
        background-color: #DEF7EC;
        color: #03543F;
    }

    :global(.status-pending) {
        background-color: #FEF3C7;
        color: #92400E;
    }

    :global(.status-rejected) {
        background-color: #FEE2E2;
        color: #991B1B;
    }

    :global(.status-processed) {
        background-color: #E0E7FF;
        color: #3730A3;
    }

    :global(.status-paid) {
        background-color: #E6F4EA;
        color: #34A853;
    }
</style> 